import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./KeywordBenchmark.module.scss";
import ResultsView from "./Components/ResultsView/ResultsView";
import { getTopKeywordsAndInstitutions } from "Explore/Services/KeywordBenchmarking";
import { getSuggestKeywords } from "Explore/Services/Common";
import useDebounce from "Explore/Components/UseDebounce/useDebounce";
import { Skeleton } from "antd";

const TYPEWRITER_PHRASES = [
  "Quantum Computing",
  "Machine Learning",
  "Climate Resilience",
  "Genomics",
  "Neuroplasticity",
  "Renewable Energy",
  "Astrophysics",
  "AI Ethics",
  "Bioinformatics",
  "Cognitive Psychology",
];

const capitalize = (str) =>
  str.replace(/\b\w/g, (c) => c.toUpperCase());

const useTypewriter = (phrases, typingSpeed = 80, deletingSpeed = 40, pauseMs = 1800) => {
  const [displayText, setDisplayText] = useState("");
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIdx] || "";

    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          if (charIdx < current.length) {
            setDisplayText(current.slice(0, charIdx + 1));
            setCharIdx((c) => c + 1);
          } else {
            setTimeout(() => setIsDeleting(true), pauseMs);
          }
        } else {
          if (charIdx > 0) {
            setDisplayText(current.slice(0, charIdx - 1));
            setCharIdx((c) => c - 1);
          } else {
            setIsDeleting(false);
            setPhraseIdx((p) => (p + 1) % phrases.length);
          }
        }
      },
      isDeleting ? deletingSpeed : typingSpeed
    );

    return () => clearTimeout(timeout);
  }, [charIdx, isDeleting, phraseIdx, phrases, typingSpeed, deletingSpeed, pauseMs]);

  return displayText;
};

const KeywordBenchmark = () => {
  const [topData, setTopData] = useState({ keywords: [], institutions: [] });
  const [isLoading, setIsLoading] = useState(true);

  const [view, setView] = useState("landing");
  const [compareResult, setCompareResult] = useState(null);

  // Inline keyword search state
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [apiSuggestions, setApiSuggestions] = useState([]);
  const inputRef = useRef(null);

  const debouncedSearchInput = useDebounce(searchInput);

  const typewriterText = useTypewriter(TYPEWRITER_PHRASES);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    getTopKeywordsAndInstitutions(15)
      .then((top) => {
        if (!isMounted) return;
        setTopData(top);
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Recommended domains from the top keywords data
  const recommendedKeywords = useMemo(() => {
    if (topData.keywords && topData.keywords.length > 0) {
      return topData.keywords.map((k) => capitalize(k.keyword));
    }
    return [];
  }, [topData.keywords]);

  // API-driven suggestions: hits /suggest/keywords/:query, dedupes against
  // already selected chips, and returns capitalized display names.
  useEffect(() => {
    const query = (debouncedSearchInput || "").trim();
    if (query.length < 2) {
      setApiSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    const controller = getSuggestKeywords(query, (res) => {
      if (cancelled) return;
      const selectedSet = new Set(
        selectedKeywords.map((kw) => kw.toLowerCase())
      );
      const items = (res || [])
        .map((item) => (item?.name || "").trim())
        .filter(Boolean)
        .filter((name) => !selectedSet.has(name.toLowerCase()))
        .slice(0, 8)
        .map((name) => capitalize(name));
      setApiSuggestions(items);
    });

    return () => {
      cancelled = true;
      controller?.abort?.();
    };
  }, [debouncedSearchInput, selectedKeywords]);

  const handleAddKeyword = useCallback(
    (keyword) => {
      const normalized = capitalize(keyword.trim());
      if (!normalized) return;
      if (!selectedKeywords.includes(normalized)) {
        setSelectedKeywords((prev) => [...prev, normalized]);
      }
      setSearchInput("");
      setApiSuggestions([]);
      setShowSuggestions(false);
    },
    [selectedKeywords]
  );

  const handleRemoveKeyword = useCallback((keyword) => {
    setSelectedKeywords((prev) => prev.filter((k) => k !== keyword));
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && searchInput.trim()) {
        e.preventDefault();
        if (apiSuggestions.length > 0) {
          handleAddKeyword(apiSuggestions[0]);
        } else {
          handleAddKeyword(searchInput.trim());
        }
      }
      if (
        e.key === "Backspace" &&
        !searchInput &&
        selectedKeywords.length > 0
      ) {
        setSelectedKeywords((prev) => prev.slice(0, -1));
      }
    },
    [searchInput, apiSuggestions, handleAddKeyword, selectedKeywords]
  );

  const handleAnalyze = useCallback(async () => {
    console.log("Analyzing keywords:", selectedKeywords);
    if (!selectedKeywords.length) return;

    setIsAnalyzing(true);
    try {
      const keywordsToSend = selectedKeywords.map((kw) => kw.toLowerCase());
      setCompareResult({ keywords: keywordsToSend });
      setView("results");
    } finally {
      setIsAnalyzing(false);
    }
  }, [selectedKeywords]);

  if (view === "results" && compareResult) {
    return (
      <div className={styles["keyword-benchmark--results"]}>
        <ResultsView initialKeywords={compareResult.keywords} admin={window.location.pathname.includes("/admin")} />
      </div>
    );
  }

  return (
    <>
      <title>Benchmark Institution by Research Topics | Academic Explorer</title>
      <meta
        name="description"
        content="Discover the top institutions and research topics by publication count, and benchmark institutions across keywords."
      />
      <div className={styles["keyword-benchmark"]}>
        {isLoading ? (
          <Skeleton paragraph={{ rows: 6 }} active />
        ) : (
          <div className={styles["centered-content"]}>
            <div className={styles["page-header"]}>
              <h1 className={styles["page-header__title"]}>
                Research Topics Benchmarking
              </h1>
              <p className={styles["page-header__description"]}>
                Explore leading institutions and trending research topics based
                on publication volume. Compare how institutions perform across
                specific research areas.
              </p>
            </div>

            <div className={styles["search-section"]}>
              <div className={styles["search-row"]}>
                <div
                  className={styles["search-bar"]}
                  onClick={() => inputRef.current?.focus()}
                >
                  <svg
                    className={styles["search-bar__icon"]}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>

                  {selectedKeywords.map((kw) => (
                    <span key={kw} className={styles["search-bar__chip"]}>
                      {kw}
                      <button
                        className={styles["search-bar__chip-remove"]}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveKeyword(kw);
                        }}
                        aria-label={`Remove ${kw}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}

                  <div className={styles["search-bar__input-wrapper"]}>
                    <input
                      ref={inputRef}
                      className={styles["search-bar__input"]}
                      type="text"
                      value={searchInput}
                      onChange={(e) => {
                        setSearchInput(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() =>
                        setTimeout(() => setShowSuggestions(false), 200)
                      }
                      onKeyDown={handleKeyDown}
                    />
                    {selectedKeywords.length === 0 && !searchInput && (
                      <span className={styles["search-bar__typewriter"]}>
                        {typewriterText}
                        <span className={styles["search-bar__cursor"]}>|</span>
                      </span>
                    )}
                  </div>

                  {/* Suggestions dropdown */}
                  {showSuggestions && apiSuggestions.length > 0 && (
                    <div className={styles["search-bar__suggestions"]}>
                      {apiSuggestions.map((kw) => (
                        <button
                          key={kw}
                          className={styles["search-bar__suggestion-item"]}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleAddKeyword(kw);
                          }}
                        >
                          {kw}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  className={styles["analyze-btn"]}
                  onClick={handleAnalyze}
                  disabled={!selectedKeywords.length || isAnalyzing}
                >
                  {isAnalyzing ? "Analyzing..." : "Analyze →"}
                </button>
              </div>

              {/* Recommended domains */}
              {recommendedKeywords.length > 0 && (
                <div className={styles["recommended"]}>
                  <span className={styles["recommended__label"]}>
                    Recommended Domains
                  </span>
                  <div className={styles["recommended__chips"]}>
                    {recommendedKeywords.map((kw) => (
                      <button
                        key={kw}
                        className={`${styles["recommended__chip"]} ${
                          selectedKeywords.includes(kw)
                            ? styles["recommended__chip--selected"]
                            : ""
                        }`}
                        onClick={() => {
                          if (selectedKeywords.includes(kw)) {
                            handleRemoveKeyword(kw);
                          } else {
                            handleAddKeyword(kw);
                          }
                        }}
                      >
                        {kw}{" "}
                        <span className={styles["recommended__chip-action"]}>
                          {selectedKeywords.includes(kw) ? "×" : "+"}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default KeywordBenchmark;
